import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PostAskRequestEmail } from 'domain/usecases';
import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';
import { makeRemotePostQueueRequestEmail } from 'main/factories/usecases';
import { AskRequest } from '@hone-automation/common';
import { invalidateQueueRequestsForLocation } from './queueQueryKeys';

export const useCreateQueueRequestEmailMutation = (
  locationId: string,
  setActiveTransaction: (trx: AskRequest) => void
) => {
  const queryClient = useQueryClient();
  const createAskRequestEmail = makeRemotePostQueueRequestEmail();

  return useMutation({
    mutationFn: async (data: PostAskRequestEmail.Body) => {
      return createAskRequestEmail.post(data);
    },
    onSuccess: async data => {
      const request = data as AskRequest;

      // Invalidate old query key format (used by useQueueRequestsQuery)
      await queryClient.invalidateQueries({ queryKey: ['queueRequests', request.status, locationId] });
      // Invalidate new query key format (used by useGetQueueRequestsQuery in QueueContext)
      invalidateQueueRequestsForLocation(queryClient, locationId);
      await queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      // Wait a moment before invalidating the email whitelist to ensure backend processing is complete
      setTimeout(async () => {
        await queryClient.invalidateQueries({ queryKey: ['emailWhitelist', locationId] });
      }, 1000);

      setActiveTransaction(request);
      showToast('Email request created successfully', TOAST_SUCCESS, FIVE_SECONDS);
    },
    onError: (e: Error) => {
      showToast(e.message, TOAST_ERROR, TEN_SECONDS);
    },
  });
};
